import clsx from "clsx";
import { ReactNode, useEffect, useRef } from "react";
import { phoneNumbers } from "shared";

import { Alert, unstable__Button as Button } from "@hagerty/react-components";

import { SettingsContainer } from "../SettingsContainer";
import { UnsavedChangesDialog } from "./components";
import { Link } from "../Link";
import { useDialog } from "../Dialog";
import { navigateToSettings } from "./utils";

import styles from "./EditPageTemplate.module.scss";

type EditPageTemplateProps = {
  children: ReactNode;
  heading: string;
  message?: string;
  unsavedChangesDialogHeading: string;
  pending: boolean;
  isFormDirty: boolean;
  hasValidationErrors: boolean;
  showMutateError: boolean;
  onClickSave: () => Promise<void>;
};

export const EditPageTemplate = ({
  children,
  heading,
  message,
  unsavedChangesDialogHeading,
  pending,
  isFormDirty,
  hasValidationErrors,
  showMutateError,
  onClickSave,
}: EditPageTemplateProps) => {
  const topOfFormRef = useRef<HTMLDivElement>(null);
  const unsavedChangesDialog = useDialog();

  useEffect(() => {
    if (showMutateError) {
      topOfFormRef.current?.scrollIntoView();
    }
  }, [showMutateError]);

  return (
    <SettingsContainer paperClassName={styles.paper}>
      <Button
        className={styles.backButton}
        aria-label="Cancel and go back"
        variant="text"
        size="small"
        iconLeft={{
          icon: "ArrowLeft",
        }}
        disabled={pending}
        onClick={onClickBack}
      >
        Back
      </Button>
      <h2 className={clsx("mb-2 mb-sm-3", styles.heading)} ref={topOfFormRef}>
        {heading}
      </h2>
      {!!message && <div className={styles.message}>{message}</div>}
      {showMutateError && (
        <Alert
          alertType="danger"
          hasIcon
          headingText="Sorry, we are unable to update your information at this time."
          messageText={
            <>
              Please try again or call <Link phoneNumber={phoneNumbers.auto} />{" "}
              for assistance.
            </>
          }
        />
      )}
      {children}
      <Button
        className={clsx(styles.saveButton, {
          "ap-loading-indicator": pending,
        })}
        aria-label="Save contact information"
        variant="solid"
        size="small"
        disabled={pending || !isFormDirty || hasValidationErrors}
        onClick={onClickSave}
      >
        {!pending && "Save changes"}
      </Button>

      {unsavedChangesDialog.isOpen && (
        <UnsavedChangesDialog
          heading={unsavedChangesDialogHeading}
          pending={pending}
          closeDialog={unsavedChangesDialog.close}
          onExit={navigateToSettings}
          onSave={onClickSave}
        />
      )}
    </SettingsContainer>
  );

  function onClickBack() {
    if (isFormDirty && !hasValidationErrors) {
      unsavedChangesDialog.open();
    } else {
      navigateToSettings();
    }
  }
};
